import * as _ from "lodash";
import * as angular from "angular";
import { TeamService } from "../services/team.service";
import { ApiUserService } from "../services/user.service";
import { TeamColorDataProvider } from "../data-provider/team-color.dp";
import { Team } from "./team.model";
import { UserService } from "../services/users.service";
import { Campaign } from "./campaign.model";
import { SubscriptionDataProvider } from "../data-provider/subscription.dp";
import { getLicenseTypeFromPermissionId } from "../utils/user-permissions";

export class User {
    public readonly requestIgnoreList = [
        "userService",
        "teamService",
        "apiUserService",
        "localStorageService",
        "subscriptionDataProvider",
        "teamColorDataProvider",
    ];
    private userService: UserService;
    private teamService: TeamService;
    private $q: any;
    private apiUserService: ApiUserService;
    private __env: any;
    private subscriptionDataProvider: SubscriptionDataProvider;
    private teamColorDataProvider: TeamColorDataProvider;
    private localStorageService: any;
    private $filter: any;

    public uid: string;
    public card: any = null;
    public teams: any = null;
    // Teams the edit dialog loaded; the server applies only the difference.
    public original_tids?: Array<number | string | null>;
    public images: any = {};

    public ufname?: string;
    public ulname?: string;
    public uemail?: string;

    public hash?: string;
    public hasprofilepic?: any;
    /** 1 when the account was created via a campaign QR sign-up code. */
    public qr_signup?: number;
    public color?: string;
    public initials?: string;
    public account_type?: string;
    public uaccountstatus?: string;
    public permissionname?: string;
    public permissionid?: any;
    public billing?: any;
    public canvasses?: any;
    public expanded?: any;
    public formattedCanvasses?: any;
    public last_canvass?: any;
    public attending?: any;
    public campaigninviteemail?: any;
    public campaigninvitephonenumber?: any;
    public campaigninvitecountrycode?: any;

    public selectedCountry?: any;
    public apptype?: any;
    public campaigninviteid?: any;
    public inviter_permission?: any;
    public ucountrycode?: any;
    public uphonenumber?: any;
    public activate?: any;
    public emails?: any;
    public countrySearchText?: any;
    public rank?: any;
    public last_active?: string;
    public campaigns?: Campaign[];
    public type?: any;
    public id?: any;
    public options?: any;
    public coords?: any;
    public permission?: any;
    public organisationinviteid?: any;
    public oid?: any;
    public email_confirmation?: any;
    public pendingemail?: any;
    public localInfo?: any;
    public license_type?: any;
    public preferences: any = {};
    public is_qr_signup?: boolean;
    public logins?: number;

    constructor(
        UserService: UserService,
        TeamService: TeamService,
        $q: any,
        $filter: any,
        ApiUserService: ApiUserService,
        __env: any,
        SubscriptionDataProvider: SubscriptionDataProvider,
        TeamColorDataProvider: TeamColorDataProvider,
        localStorageService: any,
        data?: object
    ) {
        this.userService = UserService;
        this.teamService = TeamService;
        this.$q = $q;
        this.$filter = $filter;
        this.apiUserService = ApiUserService;
        this.__env = __env;
        this.subscriptionDataProvider = SubscriptionDataProvider;
        this.teamColorDataProvider = TeamColorDataProvider;
        this.localStorageService = localStorageService;

        this.card = null;
        this.teams = null;
        this.images = {};

        if (data) {
            this.setData(data);
        }
    }

    setData(data: object): void {
        _.merge(this, data);
        this.getImage();
    }

    getID(): string {
        return this.uid;
    }

    update(): Promise<User> {
        const deferred = this.$q.defer();
        this.userService.update(this).then((response: object) => {
            let json = response["data"];
            if (json.success) {
                angular.extend(this, json.data);
                deferred.resolve(this);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    updateLicenseType(): void {
        if (!this.permission) return;

        this.license_type = getLicenseTypeFromPermissionId(
            this.permission["permissionid"]
        );
    }

    resetTeams(): void {
        this.teams = null;
    }

    removeFromTeam(team: Team): Promise<any> {
        const deferred = this.$q.defer();
        this.teamService.remove_user({ tid: team.tid, uid: this.uid }).then(
            (response: any) => {
                const json = response["data"];
                if (json.success) {
                    team.toggleMember(this);
                    this.resetTeams();
                    deferred.resolve();
                } else {
                    console.warn(json);
                    deferred.reject(json);
                }
            },
            (error) => deferred.reject(error)
        );
        return deferred.promise;
    }

    setImage(image: any): Promise<User> {
        const deferred = this.$q.defer();
        this.apiUserService
            .createImage({ image: image, hash: this.hash })
            .then((response) => {
                let json = response["data"];
                if (json.success) {
                    this.images.small =
                        this.__env.s3_bucket_url +
                        "user_images/" +
                        this.hash +
                        "_" +
                        this.uid +
                        "_50x50.png?" +
                        new Date().getTime();
                    this.images.medium =
                        this.__env.s3_bucket_url +
                        "user_images/" +
                        this.hash +
                        "_" +
                        this.uid +
                        "_100x100.png?" +
                        new Date().getTime();
                    this.images.big =
                        this.__env.s3_bucket_url +
                        "user_images/" +
                        this.hash +
                        "_" +
                        this.uid +
                        "_200x200.png?" +
                        new Date().getTime();
                    deferred.resolve(this);
                } else {
                    console.warn(json);
                    deferred.reject(json);
                }
            });
        return deferred.promise;
    }

    removeProfileImage(): Promise<object> {
        const deferred = this.$q.defer();
        this.apiUserService
            .removeImage({ hash: this.hash })
            .then((response) => {
                let json = response["data"];
                if (json.success) {
                    this.hasprofilepic = false;
                    this.getImage();
                    deferred.resolve(json);
                } else {
                    console.warn(json);
                    deferred.reject(json);
                }
            });
        return deferred.promise;
    }

    getImage(): void {
        if (this.uid) {
            let color =
                this.teamColorDataProvider.teamColours[
                    parseInt(this.uid) %
                        this.teamColorDataProvider.teamColours.length
                ]; //gets a semi random color from colors array, that should always stay the same for same user
            this.color = color;
            if (this.ufname || this.ulname) {
                const name: string = [this.ufname, this.ulname]
                    .filter(Boolean)
                    .join(" ");
                this.initials =
                    "https://eu.ui-avatars.com/api/?name=" +
                    name +
                    "&background=" +
                    color.substr(1) +
                    "&color=fff&rounded=true&bold=true";
            } else {
                this.initials =
                    "https://eu.ui-avatars.com/api/?name=Anonymised+User&background=" +
                    color.substr(1) +
                    "&color=fff&rounded=true&bold=true";
            }
            if (
                this.hasprofilepic == true ||
                this.hasprofilepic == "1" ||
                this.hasprofilepic == 1
            ) {
                this.images.small =
                    this.__env.s3_bucket_url +
                    "user_images/" +
                    this.hash +
                    "_" +
                    this.uid +
                    "_50x50.png?" +
                    new Date().getTime();
                this.images.medium =
                    this.__env.s3_bucket_url +
                    "user_images/" +
                    this.hash +
                    "_" +
                    this.uid +
                    "_100x100.png?" +
                    new Date().getTime();
                this.images.big =
                    this.__env.s3_bucket_url +
                    "user_images/" +
                    this.hash +
                    "_" +
                    this.uid +
                    "_200x200.png?" +
                    new Date().getTime();
            } else {
                this.images.small = this.initials + "&size=64";
                this.images.medium = this.initials + "&size=128";
                this.images.big = this.initials + "&size=256";
            }
        }
    }

    getCard(): Promise<any> {
        if (this.card !== null && !angular.equals(this.card, {}))
            return this.$q.resolve(this.card);
        const deferred = this.$q.defer();
        this.subscriptionDataProvider.card().then((card: any) => {
            this.card = card;
            deferred.resolve(this.card);
        });
        return deferred.promise;
    }

    getLocalStorage(): any {
        if (this.localStorageService.isSupported && this.uid) {
            let info = this.localStorageService.get("user" + this.uid);
            if (!!info && info !== "null") this.localInfo = info;
            else this.localInfo = {};
        } else {
            this.localInfo = {};
        }
        return this.localInfo;
    }

    saveLocalStorage() {
        if (this.localStorageService.isSupported) {
            this.localStorageService.set("user" + this.uid, this.localInfo);
        }
    }

    isAdmin(): boolean {
        return this.account_type === "ADMIN";
    }

    hasAdminEmail(): boolean {
        let email = this.uemail;
        return (
            email &&
            (email.includes("@vconnecta") ||
                email.includes("@civisconnect") ||
                email.includes("@voxcitio") ||
                email.includes("@ecanvasser"))
        );
    }

    setCard(card: any): Promise<any> {
        const deferred = this.$q.defer();
        deferred.resolve(card);
        this.subscriptionDataProvider.card().then((card: any) => {
            this.card = card;
            deferred.resolve(this.card);
        });
        return deferred.promise;
    }

    isOnline(): boolean {
        if (!this || (!this.last_canvass && !this.last_active)) {
            return false;
        }
        const timestamp = this.last_active ?? this.last_canvass.cstimestamp;
        return this.$filter("lastsync")(timestamp).trim() == "online";
    }

    isActiveToday(): boolean {
        if (!this || (!this.last_canvass && !this.last_active)) {
            return false;
        }
        const timestamp = this.last_active ?? this.last_canvass.cstimestamp;
        if (!timestamp) return false;
        const date = new Date(timestamp.replace(/-/g, "/")).getTime();
        const eightHoursAgo = Date.now() - 8 * 60 * 60 * 1000;

        return date >= eightHoursAgo;
    }
}
